import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { IconButton, keyframes } from '@mui/material';
import { AutumnPumpkin } from './AutumnPumpkin';
import { AutumnEasterEgg } from './AutumnEasterEgg';
import { AutumnColors } from './autumnColors';

/** Clicks must happen within this window to count as a triple click. */
export const TRIPLE_CLICK_WINDOW_MS = 1500;
export const CLICKS_TO_TRIGGER = 3;

const bounce = keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg); }
  30% { transform: translateY(-4px) rotate(-8deg); }
  60% { transform: translateY(0) rotate(8deg); }
`;

const glow = keyframes`
  0%, 100% { filter: drop-shadow(0 0 2px ${AutumnColors.GLOW}); }
  50% { filter: drop-shadow(0 0 10px ${AutumnColors.GLOW}); }
`;

/**
 * The pumpkin shown in the header. Click it three times in quick succession to trigger the easter egg.
 */
export const AutumnHeaderPumpkin: FC = () => {
  const [eggActive, setEggActive] = useState(false);
  const [bouncing, setBouncing] = useState(false);
  const clickTimes = useRef<number[]>([]);
  const bounceTimeout = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(bounceTimeout.current), []);

  const handleClick = useCallback(() => {
    const now = Date.now();
    clickTimes.current = [...clickTimes.current, now].filter((time) => now - time <= TRIPLE_CLICK_WINDOW_MS);

    // A small hop on every click so the user knows the pumpkin reacts.
    setBouncing(false);
    window.clearTimeout(bounceTimeout.current);
    // Re-trigger the animation on the next frame.
    bounceTimeout.current = window.setTimeout(() => setBouncing(true), 20);

    if (clickTimes.current.length >= CLICKS_TO_TRIGGER) {
      clickTimes.current = [];
      setEggActive(true);
    }
  }, []);

  const handleDone = useCallback(() => setEggActive(false), []);

  let animation = 'none';
  if (eggActive) {
    animation = `${glow} 1s ease-in-out infinite`;
  } else if (bouncing) {
    animation = `${bounce} 0.5s ease-out 1`;
  }

  return (
    <>
      <IconButton
        onClick={handleClick}
        aria-label='Gresskar'
        title='God høst!'
        data-testid='AutumnHeaderPumpkin'
        disableRipple
        sx={{
          ml: 1,
          p: 0.5,
          borderRadius: 2,
          animation,
          '&:hover': { backgroundColor: 'transparent', transform: 'scale(1.1)' },
          transition: 'transform 150ms ease-out',
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}>
        <AutumnPumpkin size={28} lit={eggActive} />
      </IconButton>
      {eggActive && <AutumnEasterEgg onDone={handleDone} />}
    </>
  );
};

export default AutumnHeaderPumpkin;
